import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, AlertCircle, Clock, ChevronRight, RefreshCw, FileText, ShieldCheck, History, UserCheck } from 'lucide-react';
import { V2PageShell, Card, Button, Pill, EmptyState, Toast } from '../components/v2';
import { listMyTasks, listMyTasksHistory, attestOwnership } from '../services/api';
import useFormTaskSseBus from '../hooks/useFormTaskSseBus';

/**
 * MyTasksPage — RC-227 unified inbox.
 *
 * <p>Aggregates Forms (form_task_assignments) + Attestations
 * (attestation_tasks) under one "things waiting for me" view. Server
 * does the merge + sort (by due-date ASC with NULL-due last) so the
 * page consumes one normalized row list of shape:</p>
 * <pre>
 *   { type: 'form'|'attestation', id, title, status, dueAt, openUrl, ... }
 * </pre>
 *
 * <p><b>Live updates.</b> Subscribes to the RC-224 SSE bus singleton —
 * no new EventSource. Reuses the same three form-task event names as
 * FormsToDoPage. Attestation-only changes don't fire over this stream
 * today; a follow-up RC will either extend the channel or add a
 * sibling /me/attestations stream. Until then the page lags on
 * attestation-only changes (refreshes on next nav).</p>
 *
 * <p><b>Routing-out.</b> Each row carries its own {@code openUrl} so
 * the page doesn't need to know the per-type detail-page layout —
 * forms go to /forms/to-do/&lt;id&gt;, attestations go to
 * /compliance/attestations/&lt;id&gt;. Adding a 3rd task source later
 * means one new {@code type:} value + its openUrl; this page stays
 * untouched.</p>
 *
 * <p><b>Filter chips.</b> Client-side All / Forms / Attestations filter
 * over the loaded row set. Cheap because the server already capped
 * the list to active-status tasks (CREATED + ASSIGNED) — a power user
 * with hundreds of pending tasks would be a separate scale problem.</p>
 */
export default function MyTasksPage() {
  const navigate = useNavigate();
  // 2026-07-04 (revised) — dual-list state. Active rows and history rows
  // live side-by-side so tab counts are accurate the moment the page
  // renders. Prior implementation fetched only the current tab's list,
  // which meant the History chip's count showed 0 until the operator
  // clicked into it (and All/Forms/Attestations chips showed history
  // counts after the click, if the operator switched away). Fetching
  // both in parallel on mount is cheap — active is COUNT-bounded by the
  // active predicate; history is bounded by user activity — and turns
  // the tab chips into a true always-accurate summary. Both fetches
  // are error-tolerant per source, matching the BE's per-source
  // try/catch (one source failing doesn't blank the inbox).
  const [activeRows, setActiveRows] = useState(null);   // null = not loaded yet
  const [historyRows, setHistoryRows] = useState(null); // null = not loaded yet
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // RC-OWN — accept success/error feedback
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'form' | 'attestation' | 'history'

  const load = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      // Parallel fetch — one round-trip's worth of latency instead of
      // two. Both endpoints already return {ok, data} envelopes; the
      // shape-tolerant extract mirrors what the previous single-fetch
      // implementation did.
      const [activeRes, historyRes] = await Promise.all([
        listMyTasks().catch((e) => ({ ok: false, error: e?.message })),
        listMyTasksHistory().catch((e) => ({ ok: false, error: e?.message })),
      ]);
      const extract = (res) => (
        Array.isArray(res) ? res
        : Array.isArray(res?.data) ? res.data
        : []
      );
      setActiveRows(extract(activeRes));
      setHistoryRows(extract(historyRes));
      // Surface the first error only — the other tab still renders
      // valid data even if one source failed.
      const firstErr = (activeRes && activeRes.ok === false && activeRes.error)
                    || (historyRes && historyRes.ok === false && historyRes.error);
      if (firstErr) setError(firstErr);
    } catch (e) {
      setError(e?.message || 'Could not load your tasks.');
      setActiveRows([]);
      setHistoryRows([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // RC-OWN — owner accepts accountability inline from My Tasks (NOMINATED -> ACTIVE).
  // Reuses the owner-only attest endpoint; refreshes the inbox so the row clears.
  const handleAccept = useCallback(async (row) => {
    if (!row || !row.agentId) return;
    setError(null);
    const label = row.agentName || 'this agent';
    try {
      const res = await attestOwnership(row.agentId);
      if (res && res.ok === false) throw new Error(res.error || 'Could not accept accountability.');
      setNotice({ tone: 'success', title: 'Accountability accepted',
                  description: `You are now the accountable owner of ${label}. It moves to Active.` });
      await load();
    } catch (e) {
      // Keep the row (no silent vanish); a 401 hard-redirects app-wide, but any other
      // failure surfaces here so the owner sees why the accept didn't complete.
      setNotice({ tone: 'danger', title: 'Could not accept accountability',
                  description: (e?.message || 'Please try again.') + ' If you were signed out, sign in again and retry.' });
    }
  }, [load]);

  // Legacy state name kept for the downstream render code that reads
  // "rows" — it's the visible list per current filter. Derived, not
  // stored, so tab clicks never race against fetch state.
  const rows = filter === 'history' ? historyRows : activeRows;

  // SSE — same three subscriptions as FormsToDoPage + the badges. Bus
  // singleton from RC-224 is refcounted; this hook adds a subscriber,
  // doesn't open a new connection. Debounced refetch coalesces bursts.
  const scheduleReload = useCallback(() => {
    if (scheduleReload._t) clearTimeout(scheduleReload._t);
    scheduleReload._t = setTimeout(() => { load(); }, 150);
  }, [load]);
  useFormTaskSseBus('form_task_assigned',       scheduleReload);
  useFormTaskSseBus('form_task_status_changed', scheduleReload);
  useFormTaskSseBus('form_task_reassigned',     scheduleReload);
  useEffect(() => () => {
    if (scheduleReload._t) clearTimeout(scheduleReload._t);
  }, [scheduleReload]);

  // ── Filter + counts ─────────────────────────────────────────────────
  // 2026-07-04 (revised) — counts are now derived from BOTH lists so
  // every tab chip is accurate on first paint, not just after the
  // operator clicks into it. All/Forms/Attestations chips describe
  // activeRows (the "waiting on me" set); History chip describes
  // historyRows (the terminal set).
  const counts = useMemo(() => {
    const active = activeRows || [];
    const history = historyRows || [];
    return {
      all: active.length,
      form: active.filter((r) => r.type === 'form').length,
      attestation: active.filter((r) => r.type === 'attestation').length,
      ownership: active.filter((r) => r.type === 'ownership').length,
      history: history.length,
    };
  }, [activeRows, historyRows]);

  const visibleRows = useMemo(() => {
    if (filter === 'all' || filter === 'history') return rows || [];
    return (rows || []).filter((r) => r.type === filter);
  }, [rows, filter]);

  // ── Actions ─────────────────────────────────────────────────────────
  const actions = (
    <Button variant="secondary" size="sm" iconLeft={<RefreshCw />}
            onClick={load} loading={refreshing}>
      Refresh
    </Button>
  );

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <V2PageShell
      title="My Tasks"
      subtitle="Form tasks and attestations waiting for you — fill the form or submit evidence to advance the workflow."
      actions={actions}
    >
      {error && (
        <div className="mb-4">
          <Toast tone="danger" title="Could not load tasks" description={error}
                 duration={0} onDismiss={() => setError(null)} />
        </div>
      )}
      {notice && (
        <div className="mb-4">
          <Toast tone={notice.tone} title={notice.title} description={notice.description}
                 duration={notice.tone === 'success' ? 4000 : 0} onDismiss={() => setNotice(null)} />
        </div>
      )}

      {/* Filter chip strip. History sits at the tail with a visual
          separator gap so it reads as a distinct axis (past-tense) from
          the type filters (present-tense). Same FilterChip primitive
          keeps the visual language uniform. */}
      <div className="flex flex-wrap gap-1.5 mb-4" role="tablist" aria-label="Task type filter">
        <FilterChip
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          label="All"
          count={counts.all}
        />
        <FilterChip
          active={filter === 'form'}
          onClick={() => setFilter('form')}
          icon={<FileText size={13} />}
          label="Forms"
          count={counts.form}
        />
        <FilterChip
          active={filter === 'attestation'}
          onClick={() => setFilter('attestation')}
          icon={<ShieldCheck size={13} />}
          label="Attestations"
          count={counts.attestation}
        />
        {/* Small spacer to visually separate active-vs-history axes */}
        <span aria-hidden="true" className="mx-1 w-px bg-v2-border-subtle self-stretch" />
        <FilterChip
          active={filter === 'history'}
          onClick={() => setFilter('history')}
          icon={<History size={13} />}
          label="History"
          count={counts.history}
        />
      </div>

      {rows == null && (
        <Card>
          <div className="text-[13px] text-v2-ink-muted px-1 py-2">Loading your tasks…</div>
        </Card>
      )}

      {rows != null && visibleRows.length === 0 && (
        <EmptyState
          icon={filter === 'history'
            ? <History size={32} aria-hidden="true" />
            : <Inbox size={32} aria-hidden="true" />}
          title={
            filter === 'history' ? 'No history yet'
              : filter === 'all' ? 'Inbox zero'
              : `No ${filter === 'form' ? 'forms' : 'attestations'} waiting`
          }
          description={
            filter === 'history'
              ? "Tasks you've submitted or that expired will appear here."
              : filter === 'all'
                ? 'No form tasks or attestations are waiting for you right now.'
                : 'Switch to All to see other task types.'
          }
        />
      )}

      {rows != null && visibleRows.length > 0 && (
        <Card>
          <ul className="list-none p-0 m-0 divide-y divide-v2-border-subtle">
            {visibleRows.map((row) => (
              <TaskRow key={`${row.type}:${row.id}`} row={row}
                       onOpen={() => row.openUrl && navigate(row.openUrl)}
                       onAccept={handleAccept} />
            ))}
          </ul>
        </Card>
      )}
    </V2PageShell>
  );
}

// ──────────────────────────────────────────────────────────────────────
// FilterChip — local subcomponent. Same V2-primitive contract as the
// rest of this page (Tailwind v2-token utilities only, no inline style).
// ──────────────────────────────────────────────────────────────────────

function FilterChip({ active, onClick, icon, label, count }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        'v2-focus-ring inline-flex items-center gap-1.5 px-3 py-1 ' +
        'text-[12px] font-medium rounded-v2-pill border ' +
        'transition-colors duration-v2 ease-v2 ' +
        (active
          ? 'bg-v2-action text-white border-v2-action'
          : 'bg-v2-surface text-v2-ink-secondary border-v2-border-subtle hover:bg-v2-muted hover:text-v2-ink')
      }
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      <span>{label}</span>
      <span className={
        'tabular-nums text-[11px] px-1.5 py-px rounded-v2-pill ' +
        (active ? 'bg-white/20 text-white' : 'bg-v2-muted text-v2-ink-muted')
      }>
        {count}
      </span>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TaskRow — one entry per task. Shows type chip + title + due-date
// pill + chevron. Click opens the per-type detail surface.
// ──────────────────────────────────────────────────────────────────────

function TaskRow({ row, onOpen, onAccept }) {
  const typeMeta = row.type === 'attestation'
    ? { label: 'Attestation', icon: <ShieldCheck size={12} aria-hidden="true" />, tone: 'trust' }
    : row.type === 'ownership'
      ? { label: 'Ownership', icon: <UserCheck size={12} aria-hidden="true" />, tone: 'trust' }
      : { label: 'Form', icon: <FileText size={12} aria-hidden="true" />, tone: 'action' };

  // RC-OWN — ownership accept row: attest inline (nested <button> is invalid HTML, so this
  // row is a <div> with a dedicated Accept action). 'View' deep-links to the agent.
  if (row.type === 'ownership') {
    return (
      <li>
        <div className="w-full px-4 py-3 flex items-center gap-3 bg-v2-surface">
          <span className="flex-shrink-0">
            <Pill tone={typeMeta.tone} variant="soft">
              <span className="inline-flex items-center gap-1">
                {typeMeta.icon}
                {typeMeta.label}
              </span>
            </Pill>
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[13.5px] font-semibold text-v2-ink truncate">
              {row.title}
            </span>
            <span className="block text-[11.5px] text-v2-ink-muted mt-0.5">
              {row.status || 'Awaiting your acceptance'}
            </span>
          </span>
          {row.openUrl && (
            <Button variant="ghost" size="sm" onClick={() => onOpen && onOpen()}>View</Button>
          )}
          {row.accepted
            ? <Pill tone="trust" variant="soft"><span className="inline-flex items-center gap-1"><ShieldCheck size={11} aria-hidden="true" />Accepted</span></Pill>
            : <Button variant="primary" size="sm" onClick={() => onAccept && onAccept(row)}>Accept accountability</Button>}
        </div>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={
          'v2-focus-ring w-full text-left px-4 py-3 ' +
          'flex items-center gap-3 ' +
          'bg-v2-surface hover:bg-v2-elevated ' +
          'transition-colors duration-v2 ease-v2'
        }
        aria-label={`Open ${typeMeta.label}: ${row.title}`}
      >
        <span className="flex-shrink-0">
          <Pill tone={typeMeta.tone} variant="soft">
            <span className="inline-flex items-center gap-1">
              {typeMeta.icon}
              {typeMeta.label}
            </span>
          </Pill>
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13.5px] font-semibold text-v2-ink truncate">
            {row.title}
          </span>
          <span className="block text-[11.5px] text-v2-ink-muted mt-0.5">
            {row.status || 'pending'}
          </span>
        </span>
        <DuePill dueAt={row.dueAt} />
        <ChevronRight size={16} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />
      </button>
    </li>
  );
}

/**
 * DuePill — relative-time pill (e.g. "in 2 days", "overdue 4h"). Color
 * tone shifts based on urgency: overdue → danger, due-today → warning,
 * future → action-soft. Empty when no dueAt is set.
 */
function DuePill({ dueAt }) {
  if (!dueAt) return null;
  const dueMs = new Date(dueAt).getTime();
  if (!Number.isFinite(dueMs)) return null;
  const nowMs = Date.now();
  const diffMs = dueMs - nowMs;
  const overdue = diffMs < 0;
  const dueWithin24h = !overdue && diffMs < 24 * 60 * 60 * 1000;

  let label;
  const absMs = Math.abs(diffMs);
  const days = Math.floor(absMs / (24 * 60 * 60 * 1000));
  const hours = Math.floor(absMs / (60 * 60 * 1000));
  if (days >= 1) {
    label = `${overdue ? 'Overdue ' : 'in '}${days}d`;
  } else if (hours >= 1) {
    label = `${overdue ? 'Overdue ' : 'in '}${hours}h`;
  } else {
    label = overdue ? 'Overdue' : 'Due soon';
  }

  const tone = overdue ? 'danger' : (dueWithin24h ? 'warning' : 'action');

  return (
    <span className="flex-shrink-0">
      <Pill tone={tone} variant="soft">
        <span className="inline-flex items-center gap-1">
          <Clock size={11} aria-hidden="true" />
          {label}
        </span>
      </Pill>
    </span>
  );
}
